// ============================================================
// SpringBootRoles.jsx
//
// A branded, responsive course content page for the
// "Role-Based Access Control (RBAC)" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: GrantedAuthority mapping, Method-level security,
//           and JWT claim integration.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GppGoodIcon            from "@mui/icons-material/GppGood";
import AssignmentIndIcon      from "@mui/icons-material/AssignmentInd";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import PolicyIcon             from "@mui/icons-material/Policy";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootRoles() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<GppGoodIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 18"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Role-Based <span>Access Control</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Fine-tune your application security by assigning granular permissions. Role-Based Access Control (RBAC) allows you to restrict endpoints and business logic based on user identity, ensuring only authorized personnel can access sensitive operations.
                    </Typography>
                </Box>

                {/* 1. AUTHORITIES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AssignmentIndIcon />} label="1. Defining Authorities" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        In Spring Security, roles are represented as <strong>GrantedAuthority</strong> objects. By convention, roles are prefixed with <code style={{color: BRAND.white}}>ROLE_</code>.
                    </Typography>
                    <CodeBlock
                        title="UserDetails Mapping"
                        code={`@Override\npublic Collection<? extends GrantedAuthority> getAuthorities() {\n    // Maps raw DB roles to Spring Security Authorities\n    return List.of(new SimpleGrantedAuthority("ROLE_ADMIN"));\n}`}
                    />
                </Box>

                {/* 2. METHOD SECURITY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AdminPanelSettingsIcon />} label="2. Method-Level Protection" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Enable global method security to use expression-based access control. <code style={{color: BRAND.white}}>@PreAuthorize</code> is the modern standard for evaluating permissions before a method executes.
                    </Typography>
                    <CodeBlock
                        title="Authorized Controllers"
                        code={`@PreAuthorize("hasRole('ADMIN')")\n@GetMapping("/dashboard")\npublic String adminView() { return "Sensitive Data"; }\n\n@PreAuthorize("hasAnyRole('USER', 'ADMIN')")\n@GetMapping("/profile")\npublic User getProfile() { ... }`}
                    />
                </Box>

                {/* 3. JWT CLAIMS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="3. Embedding Roles in Tokens" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        For stateless architectures, roles must be embedded as <strong>Claims</strong> within the JWT. This allows the server to verify permissions without a database lookup on every request.
                    </Typography>
                    <CodeBlock
                        title="Token Generation Logic"
                        code={`String token = Jwts.builder()\n    .setSubject(username)\n    .claim("roles", authorities.stream().map(ga -> ga.getAuthority()).toList())\n    .signWith(SignatureAlgorithm.HS512, secret)\n    .compact();`}
                    />
                </Box>

                {/* 4. CUSTOM DENIAL */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PolicyIcon />} label="4. Access Denied Handling" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        By default, Spring returns a generic 403 page. In a REST API, you should implement a custom handler to return a structured JSON error instead.
                    </Typography>
                    <CodeBlock
                        title="AccessDeniedHandler.java"
                        code={`public void handle(HttpServletRequest req, HttpServletResponse res, AccessDeniedException ex) {\n    res.setStatus(403);\n    res.setContentType("application/json");\n    res.getWriter().write("{\\"message\\": \\"Insufficient Privileges\\"}");\n}`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="RBAC Implementation Highlights" />
                    <Grid container spacing={1.5}>
                        {[
                            "Always use the ROLE_ prefix for GrantedAuthorities",
                            "Enable @EnableMethodSecurity in your configuration class",
                            "Prefer hasRole() for high-level UI/API logic",
                            "Use hasAuthority() for granular, fine-grained permissions",
                            "Centralize role names in a Constants class or Enum",
                            "Implement global exception handling for AccessDeniedExceptions"
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> <code>hasRole('ADMIN')</code> automatically checks for <code>ROLE_ADMIN</code>. If your database stores roles without the prefix, you must either add it during the mapping phase or use <code>hasAuthority('ADMIN')</code> instead.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
